import React, { useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { LineChart, Line, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, Cell } from 'recharts';
import { Badge } from '../ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import { CheckCircle2, TrendingUp, Activity } from 'lucide-react';
import { formatChartValue } from '../../utils/chartFormatters';

/**
 * Withdrawal Strategy Comparison Component
 * Compares different withdrawal strategies side-by-side
 */
export default function WithdrawalStrategyComparison({ data, currentStrategy, currencySymbol = "$" }) {
  const [viewMode, setViewMode] = useState('summary');

  if (!data || Object.keys(data).length === 0) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Withdrawal Strategy Comparison</CardTitle>
          <CardDescription>No data available</CardDescription>
        </CardHeader>
      </Card>
    );
  }

  // Strategy names mapping
  const strategyNames = {
    fixed: 'Fixed Amount',
    percentage: 'Fixed 4%',
    inflationAdjusted: 'Inflation-Adjusted',
    guardrails: 'Dynamic Guardrails',
    dynamic: 'Portfolio-Based'
  };

  // Strategy descriptions
  const strategyDescriptions = {
    fixed: 'Withdraw the same dollar amount each year, regardless of inflation or portfolio performance',
    percentage: 'Withdraw 4% of current portfolio value each year (classic percentage method)',
    inflationAdjusted: 'Start with initial amount and adjust each year for inflation (traditional approach)',
    guardrails: 'Dynamically adjust withdrawals based on portfolio performance with safety guardrails',
    dynamic: 'Flexible withdrawals that adapt to portfolio health and market conditions'
  };

  // Prepare summary data
  const summaryData = Object.keys(data).map(strategy => ({
    strategy: strategyNames[strategy] || strategy,
    strategyKey: strategy,
    finalBalance: data[strategy].finalBalance,
    success: data[strategy].success,
    avgWithdrawal: data[strategy].avgAnnualWithdrawal,
    volatility: data[strategy].volatility,
    isCurrent: strategy === currentStrategy
  }));

  // Sort by final balance
  const sortedSummaryData = [...summaryData].sort((a, b) => b.finalBalance - a.finalBalance);

  // Prepare timeline comparison data
  const timelineData = [];
  const strategies = Object.keys(data);
  const maxYears = Math.max(...strategies.map(s => data[s].projections.length));

  for (let i = 0; i < maxYears; i++) {
    const point = { year: i };
    strategies.forEach(strategy => {
      if (data[strategy].projections[i]) {
        point[strategy] = data[strategy].projections[i].totalBalance;
      }
    });
    timelineData.push(point);
  }

  // Colors for strategies
  const colors = {
    fixed: '#3b82f6',
    percentage: '#10b981',
    inflationAdjusted: '#f59e0b',
    guardrails: '#8b5cf6',
    dynamic: '#ec4899'
  };

  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      return (
        <div className="bg-white p-4 border border-gray-200 rounded shadow-lg">
          <p className="font-semibold mb-2">Year {label}</p>
          <div className="space-y-1">
            {payload.map((entry, index) => (
              <div key={index} className="flex justify-between gap-4 text-sm">
                <span style={{ color: entry.color }}>{strategyNames[entry.dataKey]}:</span>
                <span className="font-medium">
                  {currencySymbol}{entry.value.toLocaleString(undefined, { maximumFractionDigits: 0 })}
                </span>
              </div>
            ))}
          </div>
        </div>
      );
    }
    return null;
  };

  // Find best strategy
  const bestStrategy = sortedSummaryData[0];

  return (
    <Card>
      <CardHeader>
        <CardTitle>Withdrawal Strategy Comparison</CardTitle>
        <CardDescription>
          Compare different withdrawal strategies to find the best approach for your retirement
        </CardDescription>
      </CardHeader>
      <CardContent>
        <Tabs value={viewMode} onValueChange={setViewMode}>
          <TabsList className="grid w-full grid-cols-3">
            <TabsTrigger value="summary">Summary</TabsTrigger>
            <TabsTrigger value="timeline">Timeline</TabsTrigger>
            <TabsTrigger value="details">Details</TabsTrigger>
          </TabsList>

          <TabsContent value="summary" className="space-y-6">
            {/* Best Strategy Recommendation */}
            <div className="p-4 bg-green-50 border border-green-200 rounded-lg">
              <div className="flex items-start gap-3">
                <CheckCircle2 className="h-6 w-6 text-green-600 mt-0.5" />
                <div className="flex-1">
                  <h4 className="text-sm font-semibold text-green-900 mb-1">
                    Recommended Strategy: {bestStrategy.strategy}
                  </h4>
                  <p className="text-sm text-green-700">
                    This strategy provides the highest final balance ({currencySymbol}{bestStrategy.finalBalance.toLocaleString()})
                    {bestStrategy.success ? ' and successfully lasts through retirement' : ''}.
                  </p>
                </div>
              </div>
            </div>

            {/* Summary Comparison Chart */}
            <div className="h-80">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={sortedSummaryData} margin={{ top: 20, right: 30, left: 30, bottom: 80 }}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis
                    dataKey="strategy"
                    angle={-45}
                    textAnchor="end"
                    height={100}
                    interval={0}
                  />
                  <YAxis
                    label={{ value: 'Final Balance', angle: -90, position: 'insideLeft' }}
                    tickFormatter={(value) => formatChartValue(value, currencySymbol)}
                    width={100}
                  />
                  <Tooltip
                    formatter={(value) => `${currencySymbol}${value.toLocaleString(undefined, { maximumFractionDigits: 0 })}`}
                  />
                  <Bar dataKey="finalBalance" radius={[8, 8, 0, 0]}>
                    {sortedSummaryData.map((entry, index) => (
                      <Cell
                        key={`cell-${index}`}
                        fill={entry.isCurrent ? '#10b981' : colors[entry.strategyKey] || '#94a3b8'}
                        opacity={entry.isCurrent ? 1 : 0.7}
                      />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>

            {/* Strategy Cards */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {summaryData.map((strategy) => (
                <div
                  key={strategy.strategyKey}
                  className={`p-4 rounded-lg border-2 ${
                    strategy.isCurrent
                      ? 'border-green-500 bg-green-50'
                      : 'border-gray-200 bg-white'
                  }`}
                >
                  <div className="flex justify-between items-start mb-2">
                    <h4 className="font-semibold">{strategy.strategy}</h4>
                    {strategy.isCurrent && (
                      <Badge variant="success">Current</Badge>
                    )}
                  </div>
                  <div className="grid grid-cols-2 gap-3 text-sm">
                    <div>
                      <div className="text-muted-foreground">Final Balance</div>
                      <div className="font-bold text-lg">
                        {formatChartValue(strategy.finalBalance, currencySymbol)}
                      </div>
                    </div>
                    <div>
                      <div className="text-muted-foreground">Avg Withdrawal</div>
                      <div className="font-bold text-lg">
                        {formatChartValue(strategy.avgWithdrawal, currencySymbol)}
                      </div>
                    </div>
                    <div>
                      <div className="text-muted-foreground">Status</div>
                      <div className={`font-medium ${strategy.success ? 'text-green-600' : 'text-red-600'}`}>
                        {strategy.success ? '✓ Success' : '✗ Depleted'}
                      </div>
                    </div>
                    <div>
                      <div className="text-muted-foreground">Volatility</div>
                      <div className="font-medium">
                        {formatChartValue(strategy.volatility, currencySymbol)}
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </TabsContent>

          <TabsContent value="timeline">
            <div className="space-y-6">
              <div className="h-96">
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={timelineData} margin={{ top: 20, right: 30, left: 30, bottom: 20 }}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis
                      dataKey="year"
                      label={{ value: 'Years in Retirement', position: 'insideBottom', offset: -5 }}
                    />
                    <YAxis
                      label={{ value: 'Portfolio Balance', angle: -90, position: 'insideLeft' }}
                      tickFormatter={(value) => formatChartValue(value, currencySymbol)}
                      width={100}
                    />
                    <Tooltip content={<CustomTooltip />} />
                    <Legend />
                    {strategies.map((strategy) => (
                      <Line
                        key={strategy}
                        type="monotone"
                        dataKey={strategy}
                        stroke={colors[strategy]}
                        strokeWidth={strategy === currentStrategy ? 3 : 2}
                        dot={false}
                        name={strategyNames[strategy]}
                        strokeDasharray={strategy === currentStrategy ? '0' : '5 5'}
                      />
                    ))}
                  </LineChart>
                </ResponsiveContainer>
              </div>

              <div className="text-sm text-muted-foreground">
                <p>
                  <strong>Solid line:</strong> Your current strategy ({strategyNames[currentStrategy]})
                  <br />
                  <strong>Dashed lines:</strong> Alternative strategies for comparison
                </p>
              </div>
            </div>
          </TabsContent>

          <TabsContent value="details">
            <div className="space-y-6">
              {Object.keys(data).map((strategyKey) => {
                const strategy = data[strategyKey];
                const isCurrent = strategyKey === currentStrategy;

                return (
                  <div
                    key={strategyKey}
                    className={`p-6 rounded-lg border-2 ${
                      isCurrent ? 'border-green-500 bg-green-50' : 'border-gray-200'
                    }`}
                  >
                    <div className="flex justify-between items-start mb-4">
                      <div>
                        <h3 className="text-lg font-semibold flex items-center gap-2">
                          {strategyNames[strategyKey]}
                          {isCurrent && <Badge variant="success">Current</Badge>}
                        </h3>
                        <p className="text-sm text-muted-foreground mt-1">
                          {strategyDescriptions[strategyKey]}
                        </p>
                      </div>
                    </div>

                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                      <div className="p-3 bg-white rounded border">
                        <div className="text-xs text-muted-foreground mb-1">Final Balance</div>
                        <div className="text-xl font-bold">
                          {formatChartValue(strategy.finalBalance, currencySymbol)}
                        </div>
                      </div>

                      <div className="p-3 bg-white rounded border">
                        <div className="text-xs text-muted-foreground mb-1">Average Withdrawal</div>
                        <div className="text-xl font-bold">
                          {formatChartValue(strategy.avgAnnualWithdrawal, currencySymbol)}/yr
                        </div>
                      </div>

                      <div className="p-3 bg-white rounded border">
                        <div className="text-xs text-muted-foreground mb-1">Success</div>
                        <div className={`text-xl font-bold ${strategy.success ? 'text-green-600' : 'text-red-600'}`}>
                          {strategy.success ? '✓ Yes' : '✗ No'}
                        </div>
                      </div>

                      <div className="p-3 bg-white rounded border">
                        <div className="text-xs text-muted-foreground mb-1">Withdrawal Volatility</div>
                        <div className="text-xl font-bold">
                          {formatChartValue(strategy.volatility, currencySymbol)}
                        </div>
                      </div>
                    </div>

                    {/* Pros & Cons */}
                    <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <h4 className="text-sm font-semibold mb-2 text-green-700 flex items-center gap-1">
                          <TrendingUp className="h-4 w-4" />
                          Advantages
                        </h4>
                        <ul className="text-sm space-y-1 text-muted-foreground">
                          {getStrategyPros(strategyKey).map((pro, i) => (
                            <li key={i}>• {pro}</li>
                          ))}
                        </ul>
                      </div>
                      <div>
                        <h4 className="text-sm font-semibold mb-2 text-orange-700 flex items-center gap-1">
                          <Activity className="h-4 w-4" />
                          Considerations
                        </h4>
                        <ul className="text-sm space-y-1 text-muted-foreground">
                          {getStrategyCons(strategyKey).map((con, i) => (
                            <li key={i}>• {con}</li>
                          ))}
                        </ul>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </TabsContent>
        </Tabs>
      </CardContent>
    </Card>
  );
}

// Helper functions for pros and cons
function getStrategyPros(strategy) {
  const pros = {
    fixed: [
      'Simple and predictable',
      'Easy to budget',
      'No complex calculations needed'
    ],
    percentage: [
      'Automatically adjusts to portfolio performance',
      'Lower risk of portfolio depletion',
      'Based on proven 4% rule research'
    ],
    inflationAdjusted: [
      'Maintains purchasing power',
      'Predictable real income',
      'Traditional and well-tested approach'
    ],
    guardrails: [
      'Balances stability with flexibility',
      'Adjusts to market conditions',
      'Protects against severe outcomes'
    ],
    dynamic: [
      'Highly responsive to portfolio health',
      'Maximizes sustainable withdrawals',
      'Adapts to changing circumstances'
    ]
  };

  return pros[strategy] || [];
}

function getStrategyCons(strategy) {
  const cons = {
    fixed: [
      'Purchasing power erodes with inflation',
      'Ignores portfolio performance',
      'May deplete portfolio faster'
    ],
    percentage: [
      'Income varies year to year',
      'Can be volatile in market downturns',
      'Harder to budget for fixed expenses'
    ],
    inflationAdjusted: [
      'May not adjust for poor market performance',
      'Fixed withdrawal can strain portfolio',
      'Doesn\'t respond to portfolio changes'
    ],
    guardrails: [
      'More complex to implement',
      'Requires periodic adjustments',
      'Income can still fluctuate'
    ],
    dynamic: [
      'Less predictable income',
      'Requires ongoing monitoring',
      'May reduce income in down markets'
    ]
  };

  return cons[strategy] || [];
}
